import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, ProductService, UserService } from "@mypxplat/xplat/core";
import { Subject, interval, takeUntil, throttle } from "rxjs";

export type ProductSearchSubDataMode = "category" | "id";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-product-search-picker",
  templateUrl: "product-search-picker.component.html",
  standalone: false,
})
export class ProductSearchPickerComponent extends BaseComponent implements OnInit {
  // [animation] on spinner icons also binds ngbTooltip's boolean input on the same element.
  public readonly spinAnimation: any = "spin";
  @Input() products: Array<any> = [];
  @Input() sectionHeader: string;
  @Input() hint = "Search for a product by name...";
  @Input() selectedHeader: string;
  @Input() searchPlaceholder = "";
  @Input() resultLimit = 5;
  @Input() disabled = false;
  @Input() addTooltip = "Add Product";
  @Input() removeTooltip = "Remove Product";
  @Input() subDataMode: ProductSearchSubDataMode = "category";
  @Input() showProductLineSwitch = false;
  @Input() associateWithProductLine = true;
  @Input() productLineSwitchLabel = "Associate with Product Line (Recommended)";
  @Input() productLineSwitchHelp = "Toggling this on associates the content with the Fender product line, not just this individual SKU. It will apply to all SKUs in the product line.";
  @Input() removeBusyMap: Record<string | number, boolean> = {};
  @Input() associatingProduct: any = false;
  @Input() associatingMessagePrefix = "Associating";
  @Input() resultListClass = "m-b-30";
  @Input() selectedListClass = "";
  @Input() containerClass = "";

  @Output() productSelected = new EventEmitter<{ product: any; useProductLine: boolean }>();
  @Output() productRemoved = new EventEmitter<any>();
  @Output() associateWithProductLineChange = new EventEmitter<boolean>();

  public searchingProducts = false;
  public productResults: Array<any>;
  public hasFenderResults = false;

  private _productQuery: string;
  private _productQueryStream$ = new Subject<string>();

  set productQuery(query: string) {
    this._productQuery = query;
    if (this._productQuery) {
      this._productQueryStream$.next(query);
    } else {
      this.productResults = undefined;
      this.hasFenderResults = false;
    }
  }

  get productQuery() {
    return this._productQuery;
  }

  constructor(
    userService: UserService,
    private productService: ProductService
  ) {
    super(userService);
  }

  ngOnInit() {
    this._productQueryStream$
      .pipe(
        throttle(() => interval(1000), { leading: true, trailing: true }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.productAutocomplete();
      });
  }

  productAutocomplete() {
    if (!this.productQuery) {
      this.productResults = [];
      this.hasFenderResults = false;
      return;
    }
    this.searchingProducts = true;
    this.productService.searchv2("title", this.productQuery, this.resultLimit).subscribe({
      next: (result: Array<any>) => {
        this.searchingProducts = false;
        this.productResults = result || [];
        this.hasFenderResults = !!this.productResults.some((p) => !!p?.productNo);
      },
      error: () => {
        this.searchingProducts = false;
        this.productResults = [];
        this.hasFenderResults = false;
      },
    });
  }

  onProductLineToggle() {
    this.associateWithProductLine = !this.associateWithProductLine;
    this.associateWithProductLineChange.emit(this.associateWithProductLine);
  }

  selectProduct(product: any) {
    if (this.disabled) return;
    const useProductLine = this.showProductLineSwitch && this.associateWithProductLine && !!product?.productNo;
    this.productQuery = "";
    this.productResults = undefined;
    this.hasFenderResults = false;
    this.productSelected.emit({ product, useProductLine });
  }

  removeProduct(product: any) {
    if (this.disabled || this.removeBusyMap?.[product?.id]) return;
    this.productRemoved.emit(product);
  }
}
